import { useState } from "react";
import { useFormikContext } from "formik";
import { formatDate, formatDisplayDate } from "utils/formatDate";
import "react-day-picker/style.css";
import { DayPicker } from "react-day-picker";
import "./DateInput.css";
import { CustomDateDropdown } from "./CustomDateDropdown";

const CalendarInput = ({ value, onClick, placeholder }) => (
  <div
    style={{
      position: "relative",
      display: "flex",
      alignItems: "center",
    }}
  >
    <input
      type="text"
      className="form-input"
      onClick={onClick}
      value={value || ""}
      readOnly
      placeholder={placeholder || "Select date"}
      style={{ paddingRight: "40px" }}
    />
    <svg
      style={{
        position: "absolute",
        right: "12px",
        width: "20px",
        height: "20px",
        pointerEvents: "none",
        color: "#666",
      }}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="16" y1="2" x2="16" y2="6"></line>
      <line x1="8" y1="2" x2="8" y2="6"></line>
      <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
  </div>
);

const CustomInput = ({
  name,
  label,
  placeholder,
  type = "text",
  options = [],
}) => {
  const { values, setFieldValue, handleChange, handleBlur, errors, touched } =
    useFormikContext();
  const [open, setOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const maxDate = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 2);
    return d;
  })();

  const handleSelect = (option) => {
    setFieldValue(name, option);
    setOpen(false);
  };

  const handleDatePicker = () => {
    setShowDatePicker((prev) => !prev);
  };

  return (
    <div className="form-group">
      <label className="form-label">{label}</label>

      {type === "text" && (
        <input
          type="text"
          name={name}
          className="form-input"
          placeholder={placeholder}
          value={values[name] || ""}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      )}

      {type === "multiple_choice" && (
        <div className="custom-dropdown">
          <div
            className={`dropdown-header ${open ? "open" : ""}`}
            onClick={() => setOpen(!open)}
          >
            <span className="dropdown-text">
              {values[name] || placeholder || "Select an option"}
            </span>
            <svg
              className={`dropdown-arrow ${open ? "rotated" : ""}`}
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M6 9L12 15L18 9"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {open && (
            <div className="dropdown-list">
              {options.map((option) => (
                <div
                  key={option}
                  className={`dropdown-item ${
                    values[name] === option ? "selected" : ""
                  }`}
                  onClick={() => handleSelect(option)}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {type === "boolean" && (
        <div className="yes-no-buttons">
          {["Yes", "No"].map((option) => {
            const isYes = option === "Yes";
            const isSelected =
              (isYes && values[name] === true) ||
              (!isYes && values[name] === false);

            return (
              <button
                type="button"
                key={option}
                className={`yes-no-btn ${isSelected ? "selected" : ""}`}
                onClick={() => setFieldValue(name, isYes ? true : false)}
              >
                {option}
              </button>
            );
          })}
        </div>
      )}

      {type === "date" && (
        <div className="date-picker-wrapper">
          <CalendarInput
            placeholder={placeholder}
            onClick={handleDatePicker}
            value={formatDisplayDate(values[name])}
          />
          {showDatePicker && (
            <div className="date-picker-popover">
              <DayPicker
                mode="single"
                selected={values[name] ? new Date(values[name]) : undefined}
                defaultMonth={values[name] ? new Date(values[name]) : undefined}
                onSelect={(date) => {
                  setFieldValue(name, date ? formatDate(date) : "");
                  setShowDatePicker(false);
                }}
                captionLayout="dropdown"
                disabled={{ after: maxDate }}
                startMonth={new Date(1980, 0)}
                endMonth={maxDate}
                components={{ Dropdown: CustomDateDropdown }}
              />
            </div>
          )}
        </div>
      )}

      {errors[name] && touched[name] && (
        <p className="form-error">{errors[name]}</p>
      )}
    </div>
  );
};

export default CustomInput;
